import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useDispatch, useSelector } from "react-redux";
import { raiseTicket } from "../../store/actions/chatbot";
import { API_BASE_URL } from "../../utilities";

const CATEGORIES = [
  { value: "appointments", label: "Appointments" },
  { value: "billing",      label: "Billing" },
  { value: "staff",        label: "Staff" },
  { value: "inventory",    label: "Inventory" },
  { value: "technical",    label: "Technical" },
  { value: "other",        label: "Other" },
];

function TicketForm() {
  const dispatch  = useDispatch();
  const sessionId = useSelector((s) => s.chatbot.currentSessionId);

  const [form, setForm]           = useState({ subject: "", description: "", category: "appointments" });
  const [errors, setErrors]       = useState({});
  const [loading, setLoading]     = useState(false);
  const [submitted, setSubmitted] = useState(null);

  // Images stored locally — NOT uploaded until submit is clicked
  const fileInputRef         = useRef(null);
  const [images, setImages]  = useState([]); // [{ file, preview }]

  // Upload a single file and return { s3_key, url, file_name }
  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const res  = await fetch(`${API_BASE_URL}chatbot/ticket/upload/`, {
      method:  "POST",
      headers: { Authorization: localStorage.getItem("token") },
      body:    formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Upload failed");
    return data; // { s3_key, url, file_name }
  };

  const set = (field) => (e) => {
    setForm((p) => ({ ...p, [field]: e.target.value }));
    setErrors((p) => ({ ...p, [field]: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!form.subject.trim())     errs.subject     = "Subject is required.";
    if (!form.description.trim()) errs.description = "Description is required.";
    if (!form.category)           errs.category    = "Please select a category.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setImages((prev) => [...prev, ...newImages]);
    e.target.value = "";
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);

    try {
      // Upload all selected images to S3 on submit, collect full attachment objects
      const attachment_urls = [];
      for (const img of images) {
        try {
          const result = await uploadFile(img.file);
          attachment_urls.push(result); // { s3_key, url, file_name }
        } catch (_) {
          // Skip failed uploads — ticket still raised without that image
        }
      }

      const result = await dispatch(raiseTicket({
        session_id:      sessionId,
        subject:         form.subject.trim(),
        description:     form.description.trim(),
        category:        form.category,
        attachment_urls,
      }));
      setSubmitted(result.ticket_id);
    } catch (err) {
      const data = err?.response?.data || {};
      const serverErrors = {};
      Object.keys(data).forEach((k) => {
        serverErrors[k] = Array.isArray(data[k]) ? data[k][0] : data[k];
      });
      if (Object.keys(serverErrors).length) {
        setErrors(serverErrors);
      } else {
        setErrors({ subject: "Something went wrong. Please try again." });
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="ticket-form-card">
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <CheckCircleOutlineIcon color="success" />
          <Typography variant="body2" fontWeight={700} color="success.main">
            Ticket raised successfully!
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Your ticket <strong>{submitted}</strong> has been created. We will get back to you soon.
          You can check its status anytime from the Help section.
        </Typography>
      </div>
    );
  }

  return (
    <div className="ticket-form-card">
      <Typography variant="body2" fontWeight={600} color="text.primary" mb={1}>
        Raise a Support Ticket
      </Typography>

      <TextField
        size="small" label="Subject *"
        value={form.subject} onChange={set("subject")}
        fullWidth error={!!errors.subject} helperText={errors.subject}
      />

      <Box>
        <Select size="small" value={form.category} onChange={set("category")} fullWidth error={!!errors.category}>
          {CATEGORIES.map((c) => (
            <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
          ))}
        </Select>
        {errors.category && <FormHelperText error>{errors.category}</FormHelperText>}
      </Box>

      <TextField
        size="small" label="Describe your issue *"
        value={form.description} onChange={set("description")}
        multiline rows={3} fullWidth
        error={!!errors.description} helperText={errors.description}
      />

      {/* Screenshot picker — images stored locally, uploaded only on submit */}
      <Box>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddPhotoAlternateIcon />}
          onClick={() => fileInputRef.current.click()}
          disabled={loading}
          sx={{ textTransform: "none", fontSize: "12px" }}
        >
          Add screenshots (optional)
        </Button>

        {images.length > 0 && (
          <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
            {images.map((img, idx) => (
              <Box
                key={idx}
                sx={{
                  position: "relative", width: 64, height: 64,
                  borderRadius: "6px", overflow: "hidden",
                  border: "1px solid #e5e7eb",
                }}
              >
                <img
                  src={img.preview} alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                <IconButton
                  size="small"
                  onClick={() => removeImage(idx)}
                  disabled={loading}
                  sx={{
                    position: "absolute", top: 0, right: 0,
                    background: "rgba(0,0,0,0.5)", color: "#fff",
                    width: 18, height: 18, p: 0,
                    "&:hover": { background: "rgba(0,0,0,0.7)" },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {errors.session_id && <FormHelperText error>{errors.session_id}</FormHelperText>}

      <Button
        variant="contained" fullWidth onClick={handleSubmit}
        disabled={loading}
        sx={{ textTransform: "none", "&.Mui-disabled": { color: "#fff" } }}
      >
        {loading
          ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
              {images.length > 0 ? "Uploading & submitting..." : "Submitting..."}
            </>
          : "Submit Ticket"}
      </Button>
    </div>
  );
}

export default TicketForm;
